import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, PaperPlaneRight, ChatCircleText, EnvelopeSimple } from "@phosphor-icons/react";

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

function Bubble({ r }) {
  const inbound = r.direction === "in";
  const isDirect = r.kind === "direct" || r.kind === "inbound";
  const body = r.body || r.subject || "";
  const text = r.channel === "email" && r.kind !== "direct" ? r.subject : body.replace(/<[^>]+>/g, "");
  return (
    <div className={`flex ${inbound ? "justify-start" : "justify-end"} mb-2`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
        inbound ? "bg-white border border-[#D6D9DE] text-[#000000]" : "bg-[#DCFCE7] border border-[#BBF7D0] text-[#14532D]"}`}>
        {r.channel === "email" && <div className="text-[10px] font-bold uppercase text-[#000000] flex items-center gap-1"><EnvelopeSimple size={11} /> Email{r.kind === "direct" ? `: ${r.subject}` : ""}</div>}
        {text}
        <div className="text-[10px] text-[#000000] mt-1 flex items-center gap-1.5">
          {fmtTime(r.created_at)}
          {!isDirect && <span className="px-1 rounded bg-[#F1F2F4] border border-[#E2E4E8]">{(r.kind || "auto").replace(/-/g, " ")}</span>}
          {r.status === "failed" && <span className="text-[#B91C1C] font-bold">failed</span>}
          {r.status === "skipped" && <span className="text-[#B45309] font-bold">not sent</span>}
        </div>
      </div>
    </div>
  );
}

export function ContactChatDialog({ contact, defaultChannel = "sms", onClose }) {
  const [rows, setRows] = useState(null);
  const [channel, setChannel] = useState(
    defaultChannel === "email" && contact.email ? "email" : contact.phone ? "sms" : "email");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = () =>
    api.get("/admin/comms/history", { params: { phone: contact.phone || "", email: contact.email || "" } })
      .then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [rows]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post("/admin/comms/send", {
        channel,
        to: channel === "sms" ? contact.phone : contact.email,
        subject,
        body: text,
        customer_id: contact.id || "",
      });
      toast.success(channel === "sms" ? "Text sent" : "Email sent");
      setText(""); setSubject("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 grid place-items-center p-3" onClick={onClose} data-testid="contact-chat-overlay">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()} data-testid="contact-chat-dialog">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E4E6EA]">
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-[#000000] truncate" data-testid="contact-chat-name">{contact.name || "Client"}</div>
            <div className="text-xs text-[#000000] truncate">{[contact.phone, contact.email].filter(Boolean).join(" · ")}</div>
          </div>
          <button className="btn-ghost p-1.5" onClick={onClose} data-testid="contact-chat-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto bg-[#F5F6F8] px-3 py-3 min-h-[220px]" data-testid="contact-chat-thread">
          {rows === null ? (
            <div className="text-sm text-[#000000] text-center mt-8">Loading history…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-[#000000] text-center mt-8">No messages yet — say hello below.</div>
          ) : rows.map((r) => <Bubble key={r.id} r={r} />)}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="border-t border-[#E4E6EA] p-3 flex flex-col gap-2">
          <div className="flex items-center gap-1.5">
            <button type="button" disabled={!contact.phone}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border cursor-pointer flex items-center gap-1 ${channel === "sms" ? "bg-[#0A0D14] text-white border-[#0A0D14]" : "bg-white text-[#000000] border-[#C4C8CE]"} ${!contact.phone ? "opacity-40 cursor-not-allowed" : ""}`}
              onClick={() => setChannel("sms")} data-testid="chat-channel-sms">
              <ChatCircleText size={13} /> Text
            </button>
            <button type="button" disabled={!contact.email}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border cursor-pointer flex items-center gap-1 ${channel === "email" ? "bg-[#0A0D14] text-white border-[#0A0D14]" : "bg-white text-[#000000] border-[#C4C8CE]"} ${!contact.email ? "opacity-40 cursor-not-allowed" : ""}`}
              onClick={() => setChannel("email")} data-testid="chat-channel-email">
              <EnvelopeSimple size={13} /> Email
            </button>
            <span className="text-[11px] text-[#000000] ml-auto">{channel === "sms" ? "sends from your business number" : "sends from your business email"}</span>
          </div>
          {channel === "email" && (
            <input className="input-base text-sm" placeholder="Subject (optional)" value={subject}
              onChange={(e) => setSubject(e.target.value)} data-testid="chat-subject-input" />
          )}
          <div className="flex items-end gap-2">
            <textarea className="input-base text-sm flex-1" rows={2} placeholder={channel === "sms" ? "Type a text…" : "Type an email…"}
              value={text} onChange={(e) => setText(e.target.value)} data-testid="chat-message-input" />
            <button type="submit" className="btn-primary p-2.5" disabled={busy || !text.trim()} data-testid="chat-send-btn" title="Send">
              <PaperPlaneRight size={18} weight="fill" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
